import { z } from 'zod/v3';

//#region Rate Shop Schemas
const RateChangeSchema = z.object({
  change: z.object({
    value: z.number(),
  }),
  days: z.number(),
});

const CellMetaSchema = z.object({
  data: z
    .object({
      changes: z.array(RateChangeSchema),
    })
    .optional(),
  competitor_name: z.string().optional(),
  header: z.string(),
  key: z.string().optional(),
  tooltip: z.string().optional(),
});

const CellSchema = z.object({
  dataKey: z.string(),
  displayValue: z.string(),
  stayDate: z.string(),
  value: z.number(),
  meta: CellMetaSchema,
});

export const RateShopDataSchema = z.object({
  headers: z.array(z.string()),
  rows: z.array(z.array(CellSchema)),
});

export const CompetitorNoRateSchema = z.object({
  competitorName: z
    .string()
    .describe('The name of the competitor hotel that has no rate available'),
  previousRate: z
    .number()
    .describe("The competitor hotel's last known rate before they sold out or became unavailable"),
});

export const RateChangeOutputSchema = z.object({
  competitorName: z
    .string()
    .describe('The name of the competitor hotel that had a significant rate change'),
  prevRate: z.number().describe('The previous rate the competitor was selling at (from 1 day ago)'),
  newRate: z.number().describe('The current rate the competitor is selling at'),
  rateDiff: z
    .number()
    .describe(
      'The difference between new and previous rate. Negative means price decreased, positive means price increased',
    ),
  percentChange: z
    .number()
    .describe(
      'The percentage change between new and previous rate. Negative means price decreased, positive means price increased',
    )
    .nullable(),
});

export const DateEntrySchema = z.object({
  noRateCompetitors: z
    .array(CompetitorNoRateSchema)
    .describe(
      'List of competitors who have no rates available while our client does have availability',
    ),
  significantChanges: z
    .array(RateChangeOutputSchema)
    .describe(
      'List of significant rate changes (>$10 or availability changes) by competitors for this date',
    ),
});

export const CompetitorEntrySchema = z.object({
  noRatesDates: z
    .array(
      z.object({
        date: z.string().describe("The date (YYYY-MM-DD) and day of week (e.g. '2025-03-20 Thu')"),
        previousRate: z
          .number()
          .describe("The competitor hotel's last known rate before they sold out on this date"),
      }),
    )
    .describe('Dates when this competitor had no rates available while our client did'),
  significantChanges: z
    .array(
      z.object({
        date: z.string().describe("The date (YYYY-MM-DD) and day of week (e.g. '2025-03-20 Thu')"),
        prevRate: z.number().describe('The previous rate from 1 day ago'),
        newRate: z.number().describe('The current rate'),
        rateDiff: z
          .number()
          .describe(
            'The price difference (new - previous). Negative means price decreased, positive means increased',
          ),
        percentChange: z
          .number()
          .describe(
            'The percentage change between new and previous rate. Negative means price decreased, positive means price increased',
          )
          .nullable(),
      }),
    )
    .describe(
      'List of significant rate changes (>$10 or availability changes) for this competitor',
    ),
});

export const RateAnalysisSchema = z.object({
  byDate: z
    .record(DateEntrySchema)
    .describe(
      'Analysis organized by date (YYYY-MM-DD) with day of week, showing competitors with no rates and significant rate changes for each date',
    ),
  byCompetitor: z
    .record(CompetitorEntrySchema)
    .describe(
      'Analysis organized by competitor name, showing dates with no rates and significant rate changes for each competitor',
    ),
});
//#endregion

//#region Input Data Schemas
export const RmRecStatusSchema = z.enum([
  'OPEN',
  'PROCESSING',
  'ERROR',
  'ACCEPTED',
  'DECLINED',
  'UPLOADED',
]);

export const RmRecsDataSchema = z.object({
  id: z.coerce.number(),
  notes: z.string().optional().nullable(),
  rate: z.number().nullable(),
  status: RmRecStatusSchema,
  stay_date: z.string().date(),
});

export const GroupDataSchema = z.object({
  stay_date: z.string().date(),
  details: z.array(
    z.object({
      description: z.string().optional().nullable(),
      group_rooms_committed: z.number(),
      group_rooms_remaining: z.number(),
      group_rooms_sold: z.number(),
      is_past: z.boolean(),
      lowest_group_rate: z.number().optional().nullable(),
      rate_code: z.string().optional().nullable(),
      snapshot_date: z.string().optional().nullable(),
    }),
  ),
});

export const MeetingTypeSchema = z.enum(['DAILY', 'WEEKLY', 'ADHOC']);

export const GenMeetingNotesPayloadSchema = z.object({
  action: z.literal('genMeetingNotes'),
  type: MeetingTypeSchema,
  data: z.object({
    brandCode: z.string().optional(),
    userId: z.string().optional(),
    existingNotes: z.string().optional(),
    processedSections: z
      .object({
        keyMetrics: z
          .object({
            hasAlerts: z.boolean(),
            oooMessage: z.string().optional(),
            deltaMessage: z.string().optional(),
          })
          .optional(),
        rateShop: z
          .object({
            hasChanges: z.boolean(),
            summaryItems: z.array(z.string()),
          })
          .optional(),
        rmRecs: z
          .object({
            hasRecommendations: z.boolean(),
            summary: z.string(),
          })
          .optional(),
        groups: z
          .object({
            hasGroups: z.boolean(),
            summaryItems: z.array(z.string()),
          })
          .optional(),
        newGroups: z
          .object({
            hasNewGroups: z.boolean(),
            summaryItems: z.array(z.string()),
          })
          .optional(),
      })
      .optional(),
  }),
});
//#endregion

//#region Output Schemas - Match Backend Exactly
export const MeetingNotesSchema = z.object({
  lastNight: z
    .object({
      title: z.literal('Last Night Performance Summary').optional(),
      summary: z.string().optional(),
    })
    .optional(),
  rateShops: z
    .object({
      title: z.string().optional(),
      summaryItems: z.array(z.string()).optional(),
    })
    .optional(),
  rmRecs: z
    .object({
      title: z.literal('Rate Recommendations').optional(),
      summary: z.string().optional(),
    })
    .optional(),
  keyMetrics: z
    .object({
      title: z.literal('Key Metrics').optional(),
      summary: z.string().optional(),
      metrics: z.object({
        ooo: z.object({
          hasAlert: z.boolean().optional(),
          alertMessage: z.string().optional(),
        }),
        delta_1day: z.object({
          hasAlert: z.boolean().optional(),
          alertMessage: z.string().optional(),
        }),
      }),
    })
    .optional(),
  groups: z
    .object({
      title: z.string().optional(),
      summary: z.string().optional(),
      summaryItems: z.array(z.string()),
    })
    .optional(),
  newGroups: z
    .object({
      title: z.string().optional(),
      summaryItems: z.array(z.string()).optional(),
    })
    .optional()
    .describe('New group blocks added yesterday (or since Friday for Monday meetings)'),
  additionalNotes: z
    .object({
      title: z.literal('Additional Notes').optional(),
      notes: z.array(z.string()).optional(),
    })
    .optional(),
});
//#endregion

//#region Type Exports
export type RateShopData = z.infer<typeof RateShopDataSchema>;
export type RateAnalysis = z.infer<typeof RateAnalysisSchema>;
export type RmRecsData = z.infer<typeof RmRecsDataSchema>;
export type GroupData = z.infer<typeof GroupDataSchema>;
export type MeetingType = z.infer<typeof MeetingTypeSchema>;
export type GenMeetingNotesPayload = z.infer<typeof GenMeetingNotesPayloadSchema>;
export type MeetingNotesObject = z.infer<typeof MeetingNotesSchema>;
//#endregion
